/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import Switch from '../components/switch/Switch';
import Tooltip from '../components/tooltip/Tooltip';
import {logThemeToggleClick} from './DLSThemeLogger';
import {dlsAlt, dlsColor} from './theme';
import {multiThemeEnabled, setCurrentTheme} from './ThemeStore';
import {DLSThemes} from './types';
import {useThemeContext} from './useThemeContext';

const useStyles = makeStyles(theme => ({
  container : {
    alignItems  : 'center',
    display     : 'flex',
    height      : 34,
    marginRight : 12
  },
  label : {
    color         : dlsColor('grey3'),
    fontFamily    : 'Roboto',
    fontSize      : 16,
    fontWeight    : 400,
    letterSpacing : 0,
    whiteSpace    : 'nowrap'
  }
}));

// the switch sits as close to its label as the grids' View Subgroups does ...
const SWITCH_LABEL_OFFSET = '-8px';

// the tooltip's own width, its padding inside it - exactly this, never
// narrower or wider, so its text always runs to the same three lines ...
const TOOLTIP_WIDTH = 170;

// the tooltip sits 4px nearer the switch than MUI places it - up, as it
// opens below the header ...
const TOOLTIP_POPPER_PROPS = {
  modifiers : {
    offset : {
      offset : '0, -4'
    },
    // always below the switch, never flipped above it ...
    flip : {
      enabled : false
    }
  }
};

const TOOLTIP_PLACEMENT = 'bottom' as const;

// the legacy theme tooltip's colours and arrow, held to the exact width and
// the type the design gives it, its lines centred both ways ...
const useTooltipStyles = makeStyles(() => ({
  tooltip : {
    // MUI's own tooltip rule (its font, a 500 weight and a 300px max width)
    // is injected after this sheet - the doubled class outranks it, so the
    // design's width and type hold exactly ...
    '&&' : {
      alignItems      : 'center',
      backgroundColor : dlsAlt('colors', 'tooltipBackground'),
      boxSizing       : 'border-box',
      color           : dlsColor('white'),
      display         : 'flex',
      fontFamily      : 'Roboto',
      // the themes' own MUI tooltip override sets 16px/24px as !important ...
      fontSize        : '12px !important',
      fontWeight      : 400,
      justifyContent  : 'center',
      letterSpacing   : '0.4px',
      lineHeight      : '16px !important',
      maxWidth        : TOOLTIP_WIDTH,
      minWidth        : TOOLTIP_WIDTH,
      padding         : '4px 8px',
      textAlign       : 'center',
      width           : TOOLTIP_WIDTH
    }
  },
  arrow : {
    color : dlsAlt('colors', 'tooltipBackground')
  }
}));

/**
 * Displays the legacy theme header's "Try the new iOn" switch - the same
 * switch the admin grids' View Subgroups uses, with its label beside it,
 * and a tooltip over both that says what trying it means. Turning it on
 * moves the application to the current theme. Renders only while multiple
 * themes are enabled.
 *
 * ```typescript
 *
 *   import TryNewThemeSwitch from '@ion/dls/src/theme/TryNewThemeSwitch';
 *   ...
 *
 *   <TryNewThemeSwitch />
 *
 * ```
 *
 * @returns The switch and its label, or nothing while only one theme is
 *          enabled.
 */
export const TryNewThemeSwitch = () => {
  const classes        = useStyles();
  const tooltipClasses = useTooltipStyles();
  const label   = translate('common:theme.tryNew');
  const hint    = translate('common:theme.tryNewHint');
  const {
    theme,
    multiThemeEnabled : isMultiThemeEnabled
  } = useThemeContext();

  const onSwitchTheme = useCallback(function() {
    if (multiThemeEnabled.value) {
      logThemeToggleClick(theme, DLSThemes.current);
      setCurrentTheme(DLSThemes.current);
    }
  }, [theme]);

  return isMultiThemeEnabled ? (
    <Tooltip arrow
      externalClasses={tooltipClasses}
      placement={TOOLTIP_PLACEMENT}
      PopperProps={TOOLTIP_POPPER_PROPS}
      title={hint}>
      <div className={classes.container}>
        <Switch checked={false}
          marginRight={SWITCH_LABEL_OFFSET}
          inputProps={{'aria-label': label}}
          onChange={onSwitchTheme} />
        <span className={classes.label}>
          {label}
        </span>
      </div>
    </Tooltip>
  ) : null;
};

export default memo(TryNewThemeSwitch);
